import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

/** AECOM AI Use Policy document referenced by the legal warning footer. */
export const AI_USE_POLICY_URL =
  'https://aecom.sharepoint.com/sites/VivaEcoppfPnps/Policies/Artificial_Intelligence_Use_Policy_L1_009_PL1.pdf';

/** Privacy Office contact linked from the sensitive-PII bullet. */
export const PRIVACY_OFFICE_EMAIL = 'privacyquestions@aecom.com';

/**
 * Body of the AECOM document-upload legal warning: the intro line, the list of
 * document categories that must not be uploaded, and the AI Use Policy footer.
 * Rendered without a heading so callers can supply their own (e.g. a collapsible
 * toggle). Use {@link LegalWarning} for the standard heading + body block.
 */
export const LegalWarningContent: FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <p className="text-sm text-day-neutral-dark">{t('legal-warning.intro')}</p>
      <ul className="list-disc list-outside ml-5 mt-1 space-y-1 text-sm text-day-neutral-dark">
        <li>
          <Trans
            i18nKey="legal-warning.pii"
            components={{
              privacy: (
                <a
                  href={`mailto:${PRIVACY_OFFICE_EMAIL}`}
                  className="text-brand-default underline hover:text-brand-dark"
                >
                  {t('legal-warning.privacy-link')}
                </a>
              ),
            }}
          />
        </li>
        <li>{t('legal-warning.classification')}</li>
        <li>{t('legal-warning.data-sovereignty')}</li>
        <li>{t('legal-warning.ai-prohibited')}</li>
      </ul>
      <p className="mt-2 text-sm text-day-neutral-dark">
        <Trans
          i18nKey="legal-warning.footer"
          components={{
            policy: (
              <a
                href={AI_USE_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-default underline hover:text-brand-dark"
              >
                {t('legal-warning.policy-link')}
              </a>
            ),
          }}
        />
      </p>
    </>
  );
};

interface LegalWarningProps {
  /** Optional wrapper class names, e.g. spacing to fit the surrounding panel. */
  className?: string;
}

/**
 * Standard AECOM document-upload legal warning: a bold "LEGAL WARNING:" heading
 * followed by {@link LegalWarningContent}. Shared across the project setup form
 * and every skill's upload/analysis panel so the wording stays aligned.
 */
export const LegalWarning: FC<LegalWarningProps> = ({ className }) => {
  const { t } = useTranslation();

  return (
    <div className={className}>
      <p className="mb-2 font-semibold text-day-neutral-dark">{t('legal-warning.title')}</p>
      <LegalWarningContent />
    </div>
  );
};
